import { useState, useCallback, useEffect } from "react";
import { deleteSocialLinkData } from "../services/socialLinksService";

/**
 * Custom hook for managing social links sections in forms
 * Handles add, remove, and validation logic for social links sections
 */
export const useSocialLinksSections = (
  formData,
  setFormData,
  sessionId,
  maxSections = 10
) => {
  const [visibleSections, setVisibleSections] = useState(1);
  const [sectionError, setSectionError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [entries, setEntries] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);

  const fieldKeys = ["socialLinksEntryId", "socialtype", "sociallink"];

  const getRequiredFields = useCallback(
    (section) => [`socialtype${section}`, `sociallink${section}`],
    []
  );

  const isPrimaryFieldsFilled = useCallback(
    (section) => {
      const requiredFields = getRequiredFields(section);
      return requiredFields.every((fieldName) => {
        const value = formData[fieldName];
        if (typeof value === "string") {
          return value.trim() !== "";
        }
        return value !== undefined && value !== null && value !== "";
      });
    },
    [formData, getRequiredFields]
  );

  const computeVisibleSections = useCallback(
    (data) => {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxSections; i++) {
        const hasData = [`socialtype${i}`, `sociallink${i}`].some((fieldName) => {
          const value = data[fieldName];
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }
      return Math.max(1, maxFilledSection);
    },
    [maxSections]
  );

  // Clear errors when section is filled
  useEffect(() => {
    if (sectionError) {
      const currentSection = visibleSections;
      if (isPrimaryFieldsFilled(currentSection)) {
        setSectionError("");
        const fieldsToClear = getRequiredFields(currentSection);
        setFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [sectionError, visibleSections, formData, getRequiredFields, isPrimaryFieldsFilled]);

  const handleAddSection = useCallback(() => {
    if (visibleSections >= maxSections) {
      return;
    }

    const currentSection = visibleSections;
    if (!isPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = formData[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another social link.";
          });
          return next;
        });
      }

      setSectionError("Please fill the current social link details first.");
      return;
    }

    const requiredFields = getRequiredFields(currentSection);
    setFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    setVisibleSections((prev) => Math.min(prev + 1, maxSections));
    setSectionError("");
  }, [visibleSections, maxSections, isPrimaryFieldsFilled, getRequiredFields, formData]);

  const handleRemoveSection = useCallback(
    async (section) => {
      setSectionError("");

      const existingId = formData[`socialLinksEntryId${section}`];

      if (existingId && sessionId) {
        try {
          await deleteSocialLinkData(sessionId, existingId);
          setEntries((prev) => prev.filter((entry) => entry.id !== existingId));
        } catch (error) {
          console.error("Error deleting social link entry:", error);
          setSectionError("Unable to remove social link entry. Please try again.");
          return;
        }
      }

      if (existingId) {
        setRemovedIds((prev) => {
          const next = new Set(prev);
          next.add(existingId);
          return Array.from(next);
        });
      }

      setFormData((prev) => {
        const updated = { ...prev };

        for (let current = section; current <= maxSections; current++) {
          const nextSection = current + 1;

          fieldKeys.forEach((fieldKey) => {
            const nextKey = `${fieldKey}${nextSection}`;
            const currentKey = `${fieldKey}${current}`;

            if (nextSection <= maxSections) {
              updated[currentKey] = prev[nextKey] ?? "";
            } else {
              updated[currentKey] = "";
            }
          });
        }

        setVisibleSections(computeVisibleSections(updated));
        return updated;
      });

      setFieldErrors({});
    },
    [formData, sessionId, setFormData, maxSections, fieldKeys, computeVisibleSections]
  );

  return {
    visibleSections,
    setVisibleSections,
    sectionError,
    setSectionError,
    fieldErrors,
    setFieldErrors,
    entries,
    setEntries,
    removedIds,
    setRemovedIds,
    getRequiredFields,
    isPrimaryFieldsFilled,
    computeVisibleSections,
    handleAddSection,
    handleRemoveSection,
  };
};
